// The form model of the Fleet Designer card (monetization.md §3.2, R1249).
//
// The WARDROOM card edits one draft of the fleet name pool and sends it
// whole. This module holds the draft and nothing that draws, so a test
// reads it with no DOM. The meta server owns the rule of a name
// (`server/internal/fleetlook`): the client pre-checks the rune counts of
// the generated limits only, and a refusal of any other kind
// comes back from the server with a message that names the entry.

import { runeCount, tidySpaces } from '../../../util/text';
import {
  FLEET_NAME_MAX_RUNES, FLEET_NAME_MIN_RUNES, FLEET_POOL_MAX_NAMES,
} from '../../../gen/balance';

/** One draft of the pool: one text box a row. An empty row sends no
 *  name. The draft always holds one row at least, so there is a box to
 *  type in. */
export interface PoolDraft {
  rows: string[];
}

/** One refusal the client found before the save. `index` is the row. The
 *  draft never holds more rows than the pool takes (`draftOf`, `addRow`),
 *  so the client checks no count. */
export interface PoolCheck {
  index: number;
  reason: 'short' | 'long';
  /** The limit the draft broke, for the sentence. */
  limit: number;
}

/** The draft of a pool as the server answered it. */
export function draftOf(names: readonly string[]): PoolDraft {
  const rows = names.slice(0, FLEET_POOL_MAX_NAMES);
  if (rows.length === 0) rows.push('');
  return { rows };
}

/** The names a draft sends: tidied, with the empty rows and a second copy
 *  of a name that differs only in case left out. The server drops the
 *  same entries, so the counter shows what it will store. */
export function namesOf(d: PoolDraft): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of d.rows) {
    const name = tidySpaces(row);
    const key = name.toLowerCase();
    if (name === '' || seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/** True when the draft sends another list than the server holds. */
export function changed(d: PoolDraft, stored: readonly string[]): boolean {
  const a = namesOf(d);
  return a.length !== stored.length || a.some((n, i) => n !== stored[i]);
}

/** True while the draft has room for one more row. */
export function canAdd(d: PoolDraft): boolean {
  return d.rows.length < FLEET_POOL_MAX_NAMES;
}

/** Adds one empty row at the end. It reports whether a row was added. */
export function addRow(d: PoolDraft): boolean {
  if (!canAdd(d)) return false;
  d.rows.push('');
  return true;
}

/** Removes one row. The last row is emptied and not removed, so the card
 *  keeps one box. */
export function removeRow(d: PoolDraft, i: number): void {
  if (!(i >= 0 && i < d.rows.length)) return;
  if (d.rows.length === 1) {
    d.rows[0] = '';
    return;
  }
  d.rows.splice(i, 1);
}

/** The rune-count pre-check of the generated limits, or null. */
export function precheck(d: PoolDraft): PoolCheck | null {
  for (let i = 0; i < d.rows.length; i++) {
    const name = tidySpaces(d.rows[i] ?? '');
    if (name === '') continue;
    const n = runeCount(name);
    if (n < FLEET_NAME_MIN_RUNES) return { index: i, reason: 'short', limit: FLEET_NAME_MIN_RUNES };
    if (n > FLEET_NAME_MAX_RUNES) return { index: i, reason: 'long', limit: FLEET_NAME_MAX_RUNES };
  }
  return null;
}
